/**
 * La memoria dei dati della dashboard, condivisa fra i widget.
 *
 * Quattro widget sui lead fanno una sola lettura dei lead; fatturato e
 * andamento mensile una sola lettura delle fatture. Vale per una visita della
 * pagina: all'uscita si butta via, cosi' niente passa da un utente all'altro.
 *
 * Regole:
 * - chiedere una fonte gia' caricata o in caricamento non rilegge niente;
 * - un aggiornamento che fallisce tiene i dati vecchi, con l'errore accanto;
 * - se si rilegge mentre una lettura precedente e' ancora in corso, vince la
 *   piu' recente: la risposta vecchia, arrivata tardi, si ignora;
 * - ogni stato e' un oggetto nuovo solo quando cambia (serve a React per
 *   ridisegnare solo i widget toccati).
 */

export type StatoFonte<T> =
  | { stato: 'attesa' }
  | { stato: 'caricamento'; dati?: T }
  | { stato: 'pronto'; dati: T; letto: number }
  | { stato: 'errore'; errore: string; dati?: T };

export type Caricatori<M> = { [K in keyof M]: () => Promise<M[K]> };

export interface CacheFonti<M> {
  istantanea<K extends keyof M>(chiave: K): StatoFonte<M[K]>;
  iscriviti<K extends keyof M>(chiave: K, ascoltatore: () => void): () => void;
  /** Carica se non c'e' ancora niente (ne' dati ne' lettura in corso). */
  richiedi<K extends keyof M>(chiave: K): void;
  /** Rilegge comunque. */
  ricarica<K extends keyof M>(chiave: K): void;
  /** Rilegge le fonti che qualche widget sta guardando. */
  ricaricaTutto(): void;
  /** Fine visita: niente piu' letture ne' avvisi. */
  chiudi(): void;
}

const ATTESA = { stato: 'attesa' } as const;

function messaggio(errore: unknown): string {
  if (errore instanceof Error) return errore.message;
  if (typeof errore === 'object' && errore && 'message' in errore) return String((errore as { message: unknown }).message);
  return 'Errore sconosciuto';
}

export function creaCacheFonti<M>(caricatori: Caricatori<M>): CacheFonti<M> {
  const stati = new Map<keyof M, StatoFonte<unknown>>();
  const ascoltatori = new Map<keyof M, Set<() => void>>();
  const generazioni = new Map<keyof M, number>();
  let chiusa = false;

  const avvisa = (chiave: keyof M) => ascoltatori.get(chiave)?.forEach((f) => f());

  const imposta = (chiave: keyof M, stato: StatoFonte<unknown>) => {
    stati.set(chiave, stato);
    avvisa(chiave);
  };

  const datiAttuali = (chiave: keyof M): unknown => {
    const s = stati.get(chiave);
    return s && 'dati' in s ? s.dati : undefined;
  };

  const carica = (chiave: keyof M) => {
    if (chiusa) return;
    const generazione = (generazioni.get(chiave) ?? 0) + 1;
    generazioni.set(chiave, generazione);
    const vecchi = datiAttuali(chiave);
    imposta(chiave, vecchi === undefined ? { stato: 'caricamento' } : { stato: 'caricamento', dati: vecchi });

    caricatori[chiave]().then(
      (dati) => {
        if (chiusa || generazioni.get(chiave) !== generazione) return;
        imposta(chiave, { stato: 'pronto', dati, letto: Date.now() });
      },
      (errore: unknown) => {
        if (chiusa || generazioni.get(chiave) !== generazione) return;
        const precedenti = datiAttuali(chiave);
        imposta(chiave, precedenti === undefined
          ? { stato: 'errore', errore: messaggio(errore) }
          : { stato: 'errore', errore: messaggio(errore), dati: precedenti });
      },
    );
  };

  return {
    istantanea: <K extends keyof M>(chiave: K) => (stati.get(chiave) ?? ATTESA) as StatoFonte<M[K]>,
    iscriviti: (chiave, ascoltatore) => {
      const insieme = ascoltatori.get(chiave) ?? new Set();
      insieme.add(ascoltatore);
      ascoltatori.set(chiave, insieme);
      return () => { insieme.delete(ascoltatore); };
    },
    richiedi: (chiave) => {
      const s = stati.get(chiave);
      if (s && (s.stato === 'pronto' || s.stato === 'caricamento')) return;
      carica(chiave);
    },
    ricarica: (chiave) => carica(chiave),
    ricaricaTutto: () => {
      ascoltatori.forEach((insieme, chiave) => { if (insieme.size > 0) carica(chiave); });
    },
    chiudi: () => {
      chiusa = true;
      ascoltatori.clear();
    },
  };
}
